/**
 * EVENTSFLO-CREW-ONBOARDING-EMAILS-PDFS-MESSAGES-I18N-V1
 *
 * Read-only admin review card for the Crew Onboarding copy /
 * i18n catalog. Provides:
 *   - catalog list with scope/audience badges
 *   - EN / FR language toggle
 *   - sandboxed preview iframe (`sandbox=""`)
 *   - sample-variables JSON editor
 *   - dry-run-only test-send block (phrase-gated, fixed recipient)
 *
 * Strictly does NOT:
 *   - modify any DB email templates
 *   - send any real email
 *   - call SMTP
 *   - reseed templates
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { toast } from 'sonner';
import api from '../../../lib/api';

const TEST_PHRASE = 'SEND TEST CREW ONBOARDING EMAIL';
const DEFAULT_TEST_RECIPIENT = 'Eventsflo.test@hillsdon.com';

const audienceBadgeVariant = (a) =>
  a === 'crew' ? 'outline' : 'secondary';

const scopeBadgeVariant = (s) => {
  if (s === 'email') return 'default';
  if (s === 'in_app') return 'outline';
  if (s === 'pdf') return 'secondary';
  return 'destructive'; // admin_note
};

export default function CrewOnboardingCopyAuditCard() {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [language, setLanguage] = useState('en');
  const [sampleVarsText, setSampleVarsText] = useState('{}');
  const [preview, setPreview] = useState(null);
  const [phrase, setPhrase] = useState('');
  const [lastTestResult, setLastTestResult] = useState(null);

  const refreshCatalog = async () => {
    setLoading(true);
    try {
      const r = await api.get(
        '/admin/crew-onboarding/templates/catalog',
      );
      setCatalog(r.data || null);
      if (r.data?.items?.length && !selectedKey) {
        setSelectedKey(r.data.items[0].key);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Failed to load onboarding copy catalog.';
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshCatalog();
  }, []);

  const selectedEntry = useMemo(() => {
    if (!catalog || !selectedKey) return null;
    return catalog.items.find((i) => i.key === selectedKey) || null;
  }, [catalog, selectedKey]);

  const previewSelected = async () => {
    if (!selectedKey) return;
    setBusy(true);
    setPreview(null);
    try {
      let parsedVars = {};
      if (sampleVarsText.trim()) {
        try {
          parsedVars = JSON.parse(sampleVarsText);
          if (typeof parsedVars !== 'object' || Array.isArray(parsedVars)) {
            throw new Error('sample_vars must be an object');
          }
        } catch (jsonErr) {
          toast.error(
            `Sample vars JSON is invalid: ${jsonErr.message}`,
          );
          return;
        }
      }
      const r = await api.post(
        '/admin/crew-onboarding/templates/preview',
        {
          key: selectedKey,
          language,
          sample_vars: parsedVars,
        },
      );
      setPreview(r.data || null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Failed to render preview.';
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (!selectedKey) return;
    setBusy(true);
    setLastTestResult(null);
    try {
      const r = await api.post(
        '/admin/crew-onboarding/templates/test-send',
        {
          key: selectedKey,
          language,
          confirm_phrase: phrase,
        },
      );
      setLastTestResult(r.data || null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message
        || 'Dry-run test-send failed.';
      toast.error(String(msg));
    } finally {
      setBusy(false);
    }
  };

  // Build the sandboxed preview HTML — never inject JS, never use
  // dangerouslySetInnerHTML directly on the page. The iframe runs
  // with `sandbox=""` (no scripts, no forms, no popups).
  const previewHtml = useMemo(() => {
    if (!preview) return '';
    const r = preview.rendered || {};
    const title = r.title_rendered || r.subject_rendered || '';
    const body = r.body_rendered || '';
    return `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
       background:#0a0a0a;color:#e5e7eb;padding:16px;margin:0;font-size:14px;}
  h2{font-size:16px;margin:0 0 8px;color:#fafafa;}
  a{color:#67e8f9;}
  ul{padding-left:18px;}
  .meta{color:#71717a;font-size:12px;margin-bottom:12px;}
</style></head><body>
${title ? `<h2>${title}</h2>` : ''}
<div class="meta">${preview.scope || ''} • ${preview.audience || ''} • ${preview.language || ''}</div>
<div>${body}</div>
</body></html>`;
  }, [preview]);

  return (
    <Card
      id="crew-onboarding-copy-audit-panel"
      data-testid="crew-onboarding-copy-audit-card"
      className="border-zinc-800"
    >
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle data-testid="crew-onboarding-copy-audit-title">
            Crew Onboarding copy &amp; i18n catalog
          </CardTitle>
          <p
            className="text-xs text-muted-foreground mt-1"
            data-testid="crew-onboarding-copy-audit-readonly-note"
          >
            Read-only review of the reviewed EN + FR onboarding
            copy. Phase 1 does not modify DB templates and does not
            send live email.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={refreshCatalog}
          disabled={loading}
          data-testid="crew-onboarding-copy-audit-refresh-btn"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </CardHeader>

      <CardContent className="space-y-4 text-xs">
        {catalog && (
          <div
            className="text-muted-foreground"
            data-testid="crew-onboarding-copy-audit-counts"
          >
            {catalog.total_count} reviewed key{catalog.total_count === 1 ? '' : 's'}
            {' '}
            • audiences: {(catalog.audiences || []).join(', ')}
            {' '}
            • scopes: {(catalog.scopes || []).join(', ')}
          </div>
        )}

        {/* Catalog list */}
        <div
          className="border border-zinc-800 rounded max-h-56 overflow-y-auto"
          data-testid="crew-onboarding-copy-audit-list"
        >
          <table className="w-full">
            <thead className="sticky top-0 bg-zinc-900/90 text-muted-foreground">
              <tr className="text-left">
                <th className="py-1 px-2">Key</th>
                <th className="py-1 px-2">Audience</th>
                <th className="py-1 px-2">Scope</th>
              </tr>
            </thead>
            <tbody>
              {(catalog?.items || []).map((entry) => (
                <tr
                  key={entry.key}
                  className={`border-t border-zinc-800 cursor-pointer ${
                    selectedKey === entry.key ? 'bg-zinc-800/50' : ''
                  }`}
                  data-testid={`crew-onboarding-copy-row-${entry.key}`}
                  onClick={() => setSelectedKey(entry.key)}
                >
                  <td className="py-1 px-2 font-mono text-[11px]">
                    {entry.key}
                  </td>
                  <td className="py-1 px-2">
                    <Badge variant={audienceBadgeVariant(entry.audience)}>
                      {entry.audience}
                    </Badge>
                  </td>
                  <td className="py-1 px-2">
                    <Badge variant={scopeBadgeVariant(entry.scope)}>
                      {entry.scope}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Selection / preview controls */}
        {selectedEntry && (
          <div
            className="space-y-2"
            data-testid="crew-onboarding-copy-audit-preview-controls"
          >
            <div className="font-semibold">
              Selected: <code className="font-mono">{selectedEntry.key}</code>
            </div>
            <div className="text-muted-foreground">
              {selectedEntry.context}
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={language === 'en' ? 'default' : 'outline'}
                onClick={() => setLanguage('en')}
                data-testid="crew-onboarding-copy-audit-lang-en-btn"
              >
                English
              </Button>
              <Button
                size="sm"
                variant={language === 'fr' ? 'default' : 'outline'}
                onClick={() => setLanguage('fr')}
                data-testid="crew-onboarding-copy-audit-lang-fr-btn"
              >
                Français
              </Button>
              <Button
                size="sm"
                onClick={previewSelected}
                disabled={busy}
                data-testid="crew-onboarding-copy-audit-preview-btn"
              >
                Render preview
              </Button>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">
                Declared variables: {selectedEntry.variables?.join(', ') || '—'}
              </div>
              <Textarea
                value={sampleVarsText}
                onChange={(e) => setSampleVarsText(e.target.value)}
                placeholder='{"crew_firstname":"Alex","company_name":"EventsFlo Demo"}'
                rows={3}
                className="font-mono text-[11px]"
                data-testid="crew-onboarding-copy-audit-sample-vars-input"
              />
            </div>
          </div>
        )}

        {/* Sandboxed preview iframe */}
        {preview && previewHtml && (
          <div
            className="border border-zinc-800 rounded overflow-hidden"
            data-testid="crew-onboarding-copy-audit-preview-block"
          >
            {(preview.rendered?.subject_rendered) && (
              <div
                className="px-2 py-1 bg-zinc-900 border-b border-zinc-800 font-mono text-[11px]"
                data-testid="crew-onboarding-copy-audit-preview-subject"
              >
                Subject: {preview.rendered.subject_rendered}
              </div>
            )}
            <iframe
              title="onboarding-copy-preview"
              sandbox=""
              srcDoc={previewHtml}
              className="w-full h-64 bg-black"
              data-testid="crew-onboarding-copy-audit-preview-iframe"
            />
            {preview.missing_variables?.length > 0 && (
              <div
                className="px-2 py-1 text-amber-400"
                data-testid="crew-onboarding-copy-audit-preview-missing"
              >
                Missing variables (rendered as [VAR]):{' '}
                {preview.missing_variables.join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Dry-run test-send block */}
        <div
          className="border border-zinc-800 rounded p-3 space-y-2"
          data-testid="crew-onboarding-copy-audit-test-send-block"
        >
          <div className="font-semibold">Dry-run test-send</div>
          <p
            className="text-muted-foreground"
            data-testid="crew-onboarding-copy-audit-test-send-disclaimer"
          >
            Phase 1 only simulates the send. No SMTP call. No real
            email. Default recipient is the fixed test mailbox below.
          </p>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Recipient:</span>
            <Input
              value={DEFAULT_TEST_RECIPIENT}
              readOnly
              className="h-8 text-xs font-mono w-72"
              data-testid="crew-onboarding-copy-audit-test-send-recipient"
            />
          </div>
          <Input
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            placeholder={TEST_PHRASE}
            className="h-8 text-xs font-mono"
            data-testid="crew-onboarding-copy-audit-test-send-phrase-input"
          />
          <Button
            size="sm"
            onClick={sendTest}
            disabled={
              busy || phrase !== TEST_PHRASE || !selectedKey
            }
            data-testid="crew-onboarding-copy-audit-test-send-btn"
          >
            Run dry-run test
          </Button>
          {lastTestResult && (
            <pre
              className="text-[10px] bg-zinc-900/50 p-2 rounded overflow-x-auto"
              data-testid="crew-onboarding-copy-audit-test-send-result"
            >
              {JSON.stringify(lastTestResult, null, 2)}
            </pre>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
